import type { Vector3Tuple } from 'three';
import type { WorldType, Room, GameObject, Puzzle } from '../types';

// Export collision utilities
export * from './collision';

// ============================================
// VECTOR UTILITIES
// ============================================

export function addVectors(a: Vector3Tuple, b: Vector3Tuple): Vector3Tuple {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function subtractVectors(a: Vector3Tuple, b: Vector3Tuple): Vector3Tuple {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function scaleVector(v: Vector3Tuple, s: number): Vector3Tuple {
  return [v[0] * s, v[1] * s, v[2] * s];
}

export function vectorLength(v: Vector3Tuple): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

export function normalizeVector(v: Vector3Tuple): Vector3Tuple {
  const len = vectorLength(v);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

export function distanceBetween(a: Vector3Tuple, b: Vector3Tuple): number {
  return vectorLength(subtractVectors(a, b));
}

// ============================================
// WORLD UTILITIES
// ============================================

export function getOppositeWorld(world: WorldType): WorldType {
  return world === 'real' ? 'spectral' : 'real';
}

export function isVisibleInWorld(
  obj: GameObject,
  currentWorld: WorldType
): boolean {
  return obj.visibleInWorld === 'both' || obj.visibleInWorld === currentWorld;
}

export function isInteractableInWorld(
  obj: GameObject,
  currentWorld: WorldType
): boolean {
  return (
    obj.interactableInWorld === 'both' ||
    obj.interactableInWorld === currentWorld
  );
}

// ============================================
// ROOM UTILITIES
// ============================================

export function findObjectInRoom(
  room: Room,
  objectId: string
): GameObject | undefined {
  return room.objects.find((obj) => obj.id === objectId);
}

export function findPuzzleInRoom(
  room: Room,
  puzzleId: string
): Puzzle | undefined {
  return room.puzzles.find((puzzle) => puzzle.id === puzzleId);
}

export function getVisibleObjects(
  room: Room,
  currentWorld: WorldType
): GameObject[] {
  return room.objects.filter((obj) => isVisibleInWorld(obj, currentWorld));
}

export function getInteractableObjects(
  room: Room,
  currentWorld: WorldType
): GameObject[] {
  return room.objects.filter(
    (obj) =>
      isVisibleInWorld(obj, currentWorld) &&
      isInteractableInWorld(obj, currentWorld) &&
      obj.type !== 'static'
  );
}

// ============================================
// GRID UTILITIES (for isometric)
// ============================================

export function worldToGrid(
  worldPos: Vector3Tuple,
  tileSize: number
): [number, number] {
  return [
    Math.floor(worldPos[0] / tileSize),
    Math.floor(worldPos[2] / tileSize),
  ];
}

export function gridToWorld(
  gridX: number,
  gridZ: number,
  tileSize: number,
  height: number = 0
): Vector3Tuple {
  return [
    gridX * tileSize + tileSize / 2,
    height,
    gridZ * tileSize + tileSize / 2,
  ];
}

// ============================================
// COLOR UTILITIES
// ============================================

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return { r: 0, g: 0, b: 0 };
  }
  return {
    r: parseInt(result[1], 16) / 255,
    g: parseInt(result[2], 16) / 255,
    b: parseInt(result[3], 16) / 255,
  };
}

export function lerpColor(colorA: string, colorB: string, t: number): string {
  const a = hexToRgb(colorA);
  const b = hexToRgb(colorB);

  const r = Math.round((a.r + (b.r - a.r) * t) * 255);
  const g = Math.round((a.g + (b.g - a.g) * t) * 255);
  const bl = Math.round((a.b + (b.b - a.b) * t) * 255);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

// ============================================
// ID GENERATION
// ============================================

export function generateId(prefix: string = 'id'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// EASING FUNCTIONS
// ============================================

export const easing = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => --t * t * t + 1,
  easeInOutCubic: (t: number) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
};

// ============================================
// CLAMP & LERP
// ============================================

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function inverseLerp(a: number, b: number, value: number): number {
  return (value - a) / (b - a);
}

export function remap(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number {
  return lerp(outMin, outMax, inverseLerp(inMin, inMax, value));
}
